import React, { useEffect, useState } from 'react';
import { Calculator, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../utils/translations';

/**
 * Mobile-only sticky "Request Quote" CTA.
 * - Appears after the user scrolls past the hero (>400px).
 * - Hides while the user is inside the #quote section (avoid redundancy).
 * - Uses a subtle rise-in animation and safe-area padding.
 */
const MobileStickyQuoteCta = () => {
  const { language } = useLanguage();
  const t = translations[language] || translations.en;
  const isRTL = language === 'ar';

  const [visible, setVisible] = useState(false);
  const [inQuoteSection, setInQuoteSection] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setVisible(window.scrollY > 400);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    // IntersectionObserver reliably tells us when the quote section overlaps the viewport
    let observer;
    const quoteEl = document.getElementById('quote');
    if (quoteEl && typeof IntersectionObserver !== 'undefined') {
      observer = new IntersectionObserver(
        ([entry]) => setInQuoteSection(entry.isIntersecting),
        { threshold: 0, rootMargin: '-20% 0px -20% 0px' }
      );
      observer.observe(quoteEl);
    }

    return () => {
      window.removeEventListener('scroll', onScroll);
      if (observer) observer.disconnect();
    };
  }, []);

  const handleClick = () => {
    const el = document.getElementById('quote');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const shouldRender = visible && !inQuoteSection;

  return (
    <div
      className={`lg:hidden fixed left-0 right-0 z-40 px-4 pb-[max(env(safe-area-inset-bottom),0.75rem)] pt-2 pointer-events-none transition-all duration-300 ${
        shouldRender ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6 pointer-events-none'
      }`}
      style={{ bottom: 0 }}
      data-testid="mobile-sticky-cta-wrap"
    >
      <button
        onClick={handleClick}
        className="pointer-events-auto w-full h-14 rounded-full bg-gradient-to-r from-[#d4af37] to-[#c9a02f] text-[#0a1929] font-bold text-base tracking-wide shadow-[0_10px_30px_-8px_rgba(212,175,55,0.55)] active:scale-[0.98] transition-transform flex items-center justify-center gap-2 border border-[#d4af37]/40"
        data-testid="mobile-sticky-quote-cta"
        aria-label={t.hero.cta}
      >
        <Calculator className="h-5 w-5" />
        <span>{t.hero.cta}</span>
        <ArrowRight className={`h-5 w-5 ${isRTL ? 'rotate-180' : ''}`} />
      </button>
    </div>
  );
};

export default MobileStickyQuoteCta;
